<template>
	<div class="row">
		<div class="col">
			<div class="row">
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.SourceCountry.$error}">
						<label for="country">{{'Country' | trans}}</label>
						<select-group v-model="fee.SourceCountry" :list="countries" :fieldValue="'C_Country_ID'"
						              :fieldText="'Name'" id="country" :className="status($v.fee.SourceCountry)">
							<option value="">-- {{'Country' | trans}} --</option>
							<template slot="after">
								<ul class="invalid-feedback list-unstyled" v-if="$v.fee.SourceCountry.$error">
									<li v-if="!$v.fee.SourceCountry.required">
										{{'This field is required' | trans}}
									</li>
								</ul>
							</template>
						</select-group>
						<form-errors :field-name="'SourceCountry'" />
					</div>
				</div>
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.SourceCurrency.$error}">
						<label for="currency">{{'Currency' | trans}}</label>
						<select-group v-model="fee.SourceCurrency" :list="currencies" :fieldValue="'C_Currency_ID'"
						              :fieldText="'Name'" :isPending="isCurrencyPending" id="currency"
						              :className="status($v.fee.SourceCurrency)">
							<option selected value="">-- {{'Currency' | trans}} --</option>
							<template slot="after">
								<ul class="invalid-feedback list-unstyled" v-if="$v.fee.SourceCurrency.$error">
									<li v-if="!$v.fee.SourceCurrency.required">
										{{'This field is required' | trans}}
									</li>
								</ul>
							</template>
						</select-group>
						<form-errors :field-name="'SourceCurrency'" />
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.FixedFee.$error}">
						<label for="fixed-fee">{{'Fixed Fee' | trans}}</label>
						<input type="text" class="form-control" v-model="fee.FixedFee" id="fixed-fee"
						       :class="status($v.fee.FixedFee)" :disabled="isPending"
						       @input="setFixedFee($event.target.value)">
						<ul class="invalid-feedback list-unstyled" v-if="$v.fee.FixedFee.$error">
							<li v-if="!$v.fee.FixedFee.required">
								{{'This field is required' | trans}}
							</li>
							<li v-if="!$v.fee.FixedFee.decimal">
								{{'This field must be a number' | trans}}
							</li>
							<li v-if="!$v.fee.FixedFee.minValue">
								{{'This field cannot be less than ' | trans}} {{minValue}}
							</li>
						</ul>
						<form-errors :field-name="'FixedFee'" />
					</div>
				</div>
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.PercentFee.$error}">
						<label for="percent-fee">{{'Percent Fee' | trans}}</label>
						<input type="text" class="form-control" v-model="fee.PercentFee" id="percent-fee"
						       :class="status($v.fee.PercentFee)" :disabled="isPending"
						       @input="setPercentFee($event.target.value)">
						<ul class="invalid-feedback list-unstyled" v-if="$v.fee.PercentFee.$error">
							<li v-if="!$v.fee.PercentFee.required">
								{{'This field is required' | trans}}
							</li>
							<li v-if="!$v.fee.PercentFee.decimal">
								{{'This field must be a number' | trans}}
							</li>
							<li v-if="!$v.fee.PercentFee.maxValue">
								{{'This field may not be greater than ' | trans}} {{maxPercentFee}}
							</li>
							<li v-if="!$v.fee.PercentFee.minValue">
								{{'This field cannot be less than ' | trans}} {{minValue}}
							</li>
						</ul>
						<form-errors :field-name="'PercentFee'" />
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.FromAmt.$error}">
						<label for="from-amt">{{'From Amt' | trans}}</label>
						<input type="text" class="form-control" v-model="fee.FromAmt" id="from-amt"
						       :class="status($v.fee.FromAmt)" :disabled="isPending"
						       @input="setFromAmt($event.target.value)">
						<ul class="invalid-feedback list-unstyled" v-if="$v.fee.FromAmt.$error">
							<li v-if="!$v.fee.FromAmt.required">
								{{'This field is required' | trans}}
							</li>
							<li v-if="!$v.fee.FromAmt.decimal">
								{{'This field must be a number' | trans}}
							</li>
							<li v-if="!$v.fee.FromAmt.lessThan">
								{{'This field must be less than' | trans}} {{'To Amt' | trans}}
							</li>
						</ul>
						<form-errors :field-name="'FromAmt'" />
					</div>
				</div>
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.ToAmt.$error}">
						<label for="to-amt">{{'To Amt' | trans}}</label>
						<input type="text" class="form-control" v-model="fee.ToAmt" id="to-amt"
						       :class="status($v.fee.ToAmt)" :disabled="isPending"
						       @input="setToAmt($event.target.value)">
						<ul class="invalid-feedback list-unstyled" v-if="$v.fee.ToAmt.$error">
							<li v-if="!$v.fee.ToAmt.required">
								{{'This field is required' | trans}}
							</li>
							<li v-if="!$v.fee.ToAmt.decimal">
								{{'This field must be a number' | trans}}
							</li>
						</ul>
						<form-errors :field-name="'ToAmt'" />
					</div>
				</div>
			</div>
			<div class="row" v-if="payerTypes">
				<div class="col-md-6">
					<div class="form-group" :class="{ 'form-group--error': $v.fee.PayerType.$error}">
						<label for="payer-type">{{'Payer Type' | trans}}</label>
						<select-group v-model="fee.PayerType" :list="payerTypes" :fieldValue="'KEY'"
						              :fieldText="'VALUE'" id="payer-type" :className="status($v.fee.PayerType)">
							<option value="">-- {{'PayerType' | trans}} --</option>
							<template slot="after">
								<ul class="invalid-feedback list-unstyled" v-if="$v.fee.PayerType.$error">
									<li v-if="!$v.fee.PayerType.required">
										{{'This field is required' | trans}}
									</li>
								</ul>
							</template>
						</select-group>
						<form-errors :field-name="'PayerType'" />
					</div>
				</div>
			</div>
			<div class="form-group">
				<spinner-button :className="'btn btn-info'" @click="createFee" :is-pending="isPending">
					{{'Complete' | trans}}
				</spinner-button>
			</div>
		</div>
	</div>
</template>

<script>
	const { required, maxValue, decimal, minValue } = require('vuelidate/lib/validators');
	import { validationMixin } from 'vuelidate';
	import { req, ref, withParams } from 'vuelidate/lib/validators/common';
	const lessThan = (prop) => {
		return withParams({type: 'lessThan', prop}, function (value, parentVm) {
			return (req(ref(prop, this, parentVm)) && parseFloat(ref(prop, this, parentVm)) > parseFloat(value));
		});
	}

	export default {
		name: "create-fee",
		mixins: [validationMixin],
		props: ['route', 'redirectRoute', 'countries', 'payerTypes', 'fees'],
		data() {
			return {
				fee: {
					GubEzFessID: '',
					SourceCountry: '',
					SourceCurrency: '',
					PayerType: '',
					FixedFee: '',
					PercentFee: '',
					FromAmt: '',
					ToAmt: ''
				},
				maxPercentFee: 99,
				minValue: 0,
				currencies: {},
				isPending: false,
				isCurrencyPending: false,
				preLoadedSourceCurrency: null
			}
		},
		mounted() {
			this.EventBus.$on('loadEditFeeForm', (GubEzFessID) => {
				let fee = this.fees.find((item) =>
				{ return item.GUB_EZ_Fess_ID == GubEzFessID; });
				if(fee) {
					let pType = null;
					if(this.payerTypes) {
						pType = this.payerTypes.find((item) => {
							return item.VALUE == fee.PayerType;
						});
					}
					this.fee.GubEzFessID = fee.GUB_EZ_Fess_ID;
					this.fee.SourceCountry = fee.GUB_SourceCountry_ID;
					this.preLoadedSourceCurrency = fee.GUB_SourceCurrency_ID;
					this.fee.PayerType = pType? pType.KEY: '';
					this.fee.FixedFee = fee.fixed_fee;
					this.fee.PercentFee = fee.percent_fee;
					this.fee.FromAmt = fee.from_amount;
					this.fee.ToAmt = fee.toamount;
				}
			});
			this.EventBus.$on('resetCreateFeeForm', () => {
				this.$store.commit(`formErrors/${this.CONSTANTS.MUTATIONS.CLEAR_ERRORS}`);
				this.$v.$reset();
				this.resetFee();
			});
		},
		methods: {
			setFixedFee(value) {
				this.fee.FixedFee = value;
				this.$v.fee.FixedFee.$touch()
			},
			setPercentFee(value) {
				this.fee.PercentFee = value;
				this.$v.fee.PercentFee.$touch()
			},
			setFromAmt(value) {
				this.fee.FromAmt = value;
				this.$v.fee.FromAmt.$touch()
			},
			setToAmt(value) {
				this.fee.ToAmt = value;
				this.$v.fee.ToAmt.$touch()
			},
			resetFee() {
				this.fee.SourceCountry = '';
				this.fee.SourceCurrency = '';
				this.fee.PayerType = '';
				this.fee.FixedFee = '';
				this.fee.PercentFee = '';
				this.fee.FromAmt = '';
				this.fee.ToAmt = '';
			},
			async createFee() {
				this.$v.$touch();
				if (!this.$v.$invalid) {
					this.isPending = true;
					this.$store.commit(`formErrors/${this.CONSTANTS.MUTATIONS.CLEAR_ERRORS}`);
					await axios.post(this.route, this.fee)
						.then(() => {
							this.EventBus.$emit('event-success', Lang.get('Success'));
							if (this.redirectRoute) {
								setTimeout(() => window.location.href = this.redirectRoute, 2000)
							} else {
								this.$emit('feeCreated', this.fee);
								this.resetFee();
							}
						})
						.finally(() => this.isPending = false);
				}
			},
			async getCurrencies() {
				if(this.fee.SourceCountry) {
					this.isCurrencyPending = true;
					let result = await axios.get(`/countries/${this.fee.SourceCountry}/currency`)
						.finally(() => this.isCurrencyPending = false);
					this.fee.SourceCurrency = this.preLoadedSourceCurrency? this.preLoadedSourceCurrency: '';
					this.preLoadedSourceCurrency = null;
					this.currencies = result.data;
				}
			}
		},
		validations() {
			let vm = this;
			let v = {
				fee: {
					SourceCountry: {
						required
					},
					SourceCurrency: {
						required
					},
					FixedFee: {
						required,
						decimal,
						minValue: minValue(vm.minValue)
					},
					PercentFee: {
						required,
						decimal,
						maxValue: maxValue(vm.maxPercentFee),
						minValue: minValue(vm.minValue)
					},
					FromAmt: {
						required,
						decimal,
						lessThan: lessThan("ToAmt")
					},
					ToAmt: {
						required,
						decimal
					},
					PayerType: {}
				}
			};
			if(this.payerTypes) {
				v.fee.PayerType = {
					required
				};
			}
			return v;
		},
		watch: {
			'fee.SourceCountry': function () { this.getCurrencies() },
		}
	}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// resources/assets/js/components/create-fee.vue